import React, { useState, useEffect } from 'react';
import { Project, ProjectStatus, CompanyStats, Career, CarouselItem, BrandingSettings, Brand, AboutSettings, Speciality, ContactSettings } from '../types';
import { dbService } from '../dbService';
import { PROJECTS, CAREERS, INITIAL_CAROUSEL_ITEMS, DEFAULT_ABOUT_SETTINGS, DEFAULT_SPECIALITIES } from '../constants';
import { 
  Plus, 
  Trash2, 
  Edit3, 
  Image as ImageIcon, 
  Briefcase, 
  BarChart3, 
  Palette, 
  Inbox, 
  Save, 
  Upload,
  LogOut,
  Database,
  CheckCircle,
  AlertTriangle,
  MessageSquare,
  X,
  User,
  Users,
  History as HistoryIcon
} from 'lucide-react';

interface AdminDashboardProps {
  projects: Project[];
  onUpdateProjects: (projects: Project[]) => void;
  careers: Career[];
  onUpdateCareers: (careers: Career[]) => void;
  carouselItems: CarouselItem[];
  onUpdateCarousel: (items: CarouselItem[]) => void;
  companyStats: CompanyStats;
  onUpdateStats: (stats: CompanyStats) => void;
  branding: BrandingSettings;
  onUpdateBranding: (branding: BrandingSettings) => void;
  aboutSettings: AboutSettings;
  onUpdateAbout: (about: AboutSettings) => void;
  specialities: Speciality[];
  onUpdateSpecialities: (specialities: Speciality[]) => void;
  brands: Brand[];
  onUpdateBrands: (brands: Brand[]) => void;
  contactSettings: ContactSettings;
  onUpdateContact: (contact: ContactSettings) => void;
  onLogout: () => void;
}

const AdminDashboard: React.FC<AdminDashboardProps> = ({ 
  projects, 
  onUpdateProjects, 
  careers,
  onUpdateCareers,
  carouselItems,
  onUpdateCarousel,
  companyStats, 
  onUpdateStats,
  branding,
  onUpdateBranding,
  aboutSettings,
  onUpdateAbout,
  specialities,
  onUpdateSpecialities,
  brands,
  onUpdateBrands,
  contactSettings,
  onUpdateContact,
  onLogout
}) => {
  const [activeTab, setActiveTab] = useState<'projects' | 'careers' | 'carousel' | 'branding' | 'about' | 'stats' | 'leads' | 'brands' | 'specialities' | 'contact' | 'users'>('projects');
  const [isProcessing, setIsProcessing] = useState(false);
  const [inquiries, setInquiries] = useState<any[]>([]);
  const [allBrands, setAllBrands] = useState<Brand[]>([]);
  const [users, setUsers] = useState<any[]>([]);
  const [seedProgress, setSeedProgress] = useState<string | null>(null);

  const [confirmModal, setConfirmModal] = useState<{
    show: boolean;
    title: string;
    message: string;
    onConfirm: () => void;
  }>({ show: false, title: '', message: '', onConfirm: () => {} });

  const [notification, setNotification] = useState<{
    show: boolean;
    message: string;
    type: 'success' | 'error';
  }>({ show: false, message: '', type: 'success' });

  const showNotification = (message: string, type: 'success' | 'error' = 'success') => {
    setNotification({ show: true, message, type });
    setTimeout(() => setNotification(prev => ({ ...prev, show: false })), 4000);
  };

  const triggerConfirm = (title: string, message: string, onConfirm: () => void) => {
    setConfirmModal({ show: true, title, message, onConfirm });
  };

  // Form States
  const [showProjectForm, setShowProjectForm] = useState(false);
  const [editingProjectId, setEditingProjectId] = useState<string | null>(null);
  const [projectFormData, setProjectFormData] = useState<Project>({
    id: '', title: '', location: '', status: ProjectStatus.ONGOING, description: '', imageUrl: '', stats: [{ label: 'Length', value: '' }, { label: 'Timeframe', value: '' }, { label: 'Team Size', value: '' }]
  });

  const [showCareerForm, setShowCareerForm] = useState(false);
  const [editingCareerId, setEditingCareerId] = useState<string | null>(null);
  const [careerFormData, setCareerFormData] = useState<Career>({
    id: '', title: '', department: 'Operations', location: '', type: 'Full-time', description: ''
  });

  const [carouselUpload, setCarouselUpload] = useState({ imageUrl: '', description: '' });
  const [editingBrandId, setEditingBrandId] = useState<string | null>(null);
  const [brandUpload, setBrandUpload] = useState({ name: '', abbreviation: '', imageUrl: '' });
  const [brandingForm, setBrandingForm] = useState<BrandingSettings>(branding);
  const [aboutForm, setAboutForm] = useState<AboutSettings>(aboutSettings);
  const [statsForm, setStatsForm] = useState<CompanyStats>(companyStats);
  const [contactForm, setContactForm] = useState<ContactSettings>(contactSettings);

  const [showSpecialityForm, setShowSpecialityForm] = useState(false);
  const [editingSpecialityId, setEditingSpecialityId] = useState<string | null>(null);
  const [specialityFormData, setSpecialityFormData] = useState<Speciality>({
    id: '', title: '', description: '', imageUrl: ''
  });

  const generateId = () => `id-${Date.now()}-${Math.floor(Math.random() * 1000)}`;

  const loadUsers = async () => {
    try {
      const data = await dbService.getAllUsers();
      setUsers(data);
    } catch (e) {
      console.error("Load Users Error:", e);
    }
  };

  useEffect(() => {
    if (activeTab === 'leads') loadInquiries();
    if (activeTab === 'brands') loadBrands();
    if (activeTab === 'users') loadUsers();
  }, [activeTab]);

  const loadInquiries = async () => {
    setIsProcessing(true);
    try {
      const data = await dbService.getInquiries();
      setInquiries(data);
    } catch (e) {
      console.error("Failed to load inquiries", e);
    } finally {
      setIsProcessing(false);
    }
  };

  const loadBrands = async () => {
    setIsProcessing(true);
    try {
      const data = await dbService.getBrands();
      setAllBrands(data);
    } catch (e) {
      console.error("Failed to load brands", e);
    } finally {
      setIsProcessing(false);
    }
  };

  const handleSeedTemplates = async () => {
    triggerConfirm(
      "Reset Database?",
      "This will overwrite all existing cloud data with industrial road construction templates. Continue?",
      async () => {
        setIsProcessing(true);
        setSeedProgress("Initializing Industrial Database...");
        try {
          for (const p of PROJECTS) await dbService.saveProject(p);
          onUpdateProjects([...PROJECTS]);
          for (const c of CAREERS) await dbService.saveCareer(c);
          onUpdateCareers([...CAREERS]);
          for (const item of INITIAL_CAROUSEL_ITEMS) await dbService.saveCarousel(item);
          onUpdateCarousel([...INITIAL_CAROUSEL_ITEMS]);
          for (const spec of DEFAULT_SPECIALITIES) await dbService.saveSpeciality(spec);
          onUpdateSpecialities([...DEFAULT_SPECIALITIES]);
          await dbService.saveAbout(DEFAULT_ABOUT_SETTINGS);
          onUpdateAbout(DEFAULT_ABOUT_SETTINGS);
          showNotification("Infrastructure cloud successfully populated!", "success");
        } catch (e) {
          showNotification("Write failed. Check console for details.", "error");
        } finally {
          setIsProcessing(false);
          setSeedProgress(null);
        }
      }
    );
  };

  const processImage = (file: File): Promise<string> => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = (e) => {
        const img = new Image();
        img.onload = () => {
          const canvas = document.createElement('canvas');
          let width = img.width;
          let height = img.height;

          // Max dimensions for infrastructure photos
          const MAX_WIDTH = 1200;
          const MAX_HEIGHT = 1200;

          if (width > height) {
            if (width > MAX_WIDTH) {
              height *= MAX_WIDTH / width;
              width = MAX_WIDTH;
            }
          } else {
            if (height > MAX_HEIGHT) {
              width *= MAX_HEIGHT / height;
              height = MAX_HEIGHT;
            }
          }

          canvas.width = width;
          canvas.height = height;
          const ctx = canvas.getContext('2d');
          if (!ctx) {
            reject(new Error("Could not get canvas context"));
            return;
          }
          ctx.drawImage(img, 0, 0, width, height);

          // Compress as JPEG with 0.7 quality
          const dataUrl = canvas.toDataURL('image/jpeg', 0.7);
          
          // Check if still too large (Firestore limit is ~1MB)
          // base64 is ~1.33x larger than binary, so 1MB limit is ~1.3MB string
          if (dataUrl.length > 1000000) {
            // If still too large, try even lower quality
            resolve(canvas.toDataURL('image/jpeg', 0.5));
          } else {
            resolve(dataUrl);
          }
        };
        img.onerror = () => reject(new Error("Failed to load image"));
        img.src = e.target?.result as string;
      };
      reader.onerror = () => reject(new Error("Failed to read file"));
      reader.readAsDataURL(file);
    });
  };

  // --- Project Handlers ---
  const startAddProject = () => {
    setEditingProjectId(null);
    setProjectFormData({ id: generateId(), title: '', location: '', status: ProjectStatus.ONGOING, description: '', imageUrl: '', stats: [{ label: 'Length', value: '' }, { label: 'Timeframe', value: '' }, { label: 'Team Size', value: '' }] });
    setShowProjectForm(true);
  };

  const startEditProject = (p: Project) => {
    setEditingProjectId(p.id);
    setProjectFormData({ ...p });
    setShowProjectForm(true);
  };

  const saveProject = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsProcessing(true);
    try {
      const updatedProject = { ...projectFormData };
      await dbService.saveProject(updatedProject);
      if (editingProjectId) {
        onUpdateProjects(projects.map(p => p.id === editingProjectId ? updatedProject : p));
      } else {
        onUpdateProjects([updatedProject, ...projects]);
      }
      setShowProjectForm(false);
      showNotification("Project saved successfully!", "success");
    } catch (error: any) {
      console.error("Save Project Error:", error);
      if (error.message?.includes('longer than 1048487 bytes')) {
        showNotification("Image too large. Max 1MB.", "error");
      } else {
        showNotification("Failed to save project.", "error");
      }
    } finally { setIsProcessing(false); }
  };

  // --- Career Handlers ---
  const startAddCareer = () => {
    setEditingCareerId(null);
    setCareerFormData({ id: generateId(), title: '', department: 'Engineering', location: '', type: 'Full-time', description: '' });
    setShowCareerForm(true);
  };

  const startEditCareer = (c: Career) => {
    setEditingCareerId(c.id);
    setCareerFormData({ ...c });
    setShowCareerForm(true);
  };

  const saveCareer = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsProcessing(true);
    try {
      const updatedCareer = { ...careerFormData };
      await dbService.saveCareer(updatedCareer);
      if (editingCareerId) {
        onUpdateCareers(careers.map(c => c.id === editingCareerId ? updatedCareer : c));
      } else {
        onUpdateCareers([updatedCareer, ...careers]);
      }
      setShowCareerForm(false);
      showNotification("Career listing saved!", "success");
    } catch (error: any) {
      console.error("Save Career Error:", error);
      showNotification("Failed to save career.", "error");
    } finally { setIsProcessing(false); }
  };

  // --- Branding & Stats ---
  const saveBranding = async () => {
    setIsProcessing(true);
    try {
      await dbService.saveBranding(brandingForm);
      onUpdateBranding(brandingForm);
      showNotification("Branding updated!", "success");
    } catch (error: any) {
      console.error("Save Branding Error:", error);
      if (error.message?.includes('longer than 1048487 bytes')) {
        showNotification("Images too large.", "error");
      } else {
        showNotification("Update failed.", "error");
      }
    } finally { setIsProcessing(false); }
  };

  const saveAbout = async () => {
    setIsProcessing(true);
    try {
      await dbService.saveAbout(aboutForm);
      onUpdateAbout(aboutForm);
      showNotification("About Us updated!", "success");
    } catch (error: any) {
      console.error("Save About Error:", error);
      if (error.message?.includes('longer than 1048487 bytes')) {
        showNotification("Images too large.", "error");
      } else {
        showNotification("Update failed.", "error");
      }
    } finally { setIsProcessing(false); }
  };

  const saveStats = async () => {
    setIsProcessing(true);
    try {
      await dbService.saveStats(statsForm);
      onUpdateStats(statsForm);
      showNotification("Stats updated!", "success");
    } catch (error: any) {
      console.error("Save Stats Error:", error);
      showNotification("Update failed.", "error");
    } finally { setIsProcessing(false); }
  };

  const saveContact = async () => {
    setIsProcessing(true);
    try {
      await dbService.saveContact(contactForm);
      onUpdateContact(contactForm);
      showNotification("Contact details updated!", "success");
    } catch (error: any) {
      console.error("Save Contact Error:", error);
      showNotification("Update failed.", "error");
    } finally { setIsProcessing(false); }
  };

  const saveBrand = async () => {
    if (!brandUpload.name || !brandUpload.imageUrl) {
      showNotification("Name and Logo are required", "error");
      return;
    }
    setIsProcessing(true);
    try {
      const brandData: Brand = {
        id: editingBrandId || generateId(),
        name: brandUpload.name,
        abbreviation: brandUpload.abbreviation,
        imageUrl: brandUpload.imageUrl,
        addedAt: editingBrandId ? (allBrands.find(b => b.id === editingBrandId)?.addedAt || new Date().toISOString()) : new Date().toISOString()
      };
      await dbService.saveBrand(brandData);
      
      let updated: Brand[];
      if (editingBrandId) {
        updated = allBrands.map(b => b.id === editingBrandId ? brandData : b);
      } else {
        updated = [brandData, ...allBrands];
      }
      
      setAllBrands(updated);
      onUpdateBrands(updated);
      setBrandUpload({ name: '', abbreviation: '', imageUrl: '' });
      setEditingBrandId(null);
      showNotification(editingBrandId ? "Client updated!" : "Client added!", "success");
    } catch (error: any) {
      console.error("Save Brand Error:", error);
      showNotification("Failed to save brand.", "error");
    } finally {
      setIsProcessing(false);
    }
  };

  const startEditBrand = (b: Brand) => {
    setEditingBrandId(b.id);
    setBrandUpload({
      name: b.name,
      abbreviation: b.abbreviation || '',
      imageUrl: b.imageUrl
    });
    // Scroll to form
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  // --- Speciality Handlers ---
  const startAddSpeciality = () => {
    setEditingSpecialityId(null);
    setSpecialityFormData({ id: generateId(), title: '', description: '', imageUrl: '' });
    setShowSpecialityForm(true);
  };

  const startEditSpeciality = (s: Speciality) => {
    setEditingSpecialityId(s.id);
    setSpecialityFormData({ ...s });
    setShowSpecialityForm(true);
  };

  const saveSpeciality = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsProcessing(true);
    try {
      const updatedSpeciality = { ...specialityFormData };
      await dbService.saveSpeciality(updatedSpeciality);
      if (editingSpecialityId) {
        onUpdateSpecialities(specialities.map(s => s.id === editingSpecialityId ? updatedSpeciality : s));
      } else {
        onUpdateSpecialities([updatedSpeciality, ...specialities]);
      }
      setShowSpecialityForm(false);
      showNotification("Speciality saved!", "success");
    } catch (error: any) {
      console.error("Save Speciality Error:", error);
      if (error.message?.includes('longer than 1048487 bytes')) {
        showNotification("Image too large. Max 1MB.", "error");
      } else {
        showNotification("Failed to save speciality.", "error");
      }
    } finally { setIsProcessing(false); }
  };

  const addStatRow = () => {
    setProjectFormData({
      ...projectFormData,
      stats: [...projectFormData.stats, { label: '', value: '' }]
    });
  };

  const removeStatRow = (index: number) => {
    setProjectFormData({
      ...projectFormData,
      stats: projectFormData.stats.filter((_, i) => i !== index)
    });
  };

  const updateStatRow = (index: number, field: 'label' | 'value', value: string) => {
    const newStats = [...projectFormData.stats];
    newStats[index] = { ...newStats[index], [field]: value };
    setProjectFormData({ ...projectFormData, stats: newStats });
  };

  return (
    <>
      <div className="max-w-7xl mx-auto px-4 py-10 min-h-screen bg-slate-50 dark:bg-slate-950 transition-colors">
      {seedProgress && (
        <div className="fixed inset-0 z-[100] bg-blue-900/95 backdrop-blur-sm flex items-center justify-center p-8">
          <div className="text-center space-y-4">
            <div className="w-16 h-16 border-4 border-blue-400/20 border-t-white rounded-full animate-spin mx-auto"></div>
            <h2 className="text-white text-2xl font-black uppercase tracking-tighter">{seedProgress}</h2>
          </div>
        </div>
      )}

      <div className="flex flex-col md:flex-row justify-between items-start md:items-center mb-10 gap-4">
        <div>
          <h1 className="text-3xl font-black text-slate-900 dark:text-white uppercase tracking-tighter">Infrastructure Hub</h1>
          <p className="text-slate-500 dark:text-slate-400 text-sm font-medium">Operations, Personnel, and Brand Asset Management.</p>
        </div>
        <div className="flex gap-2">
          <button onClick={handleSeedTemplates} className="flex items-center gap-2 px-4 py-2 bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-300 rounded-lg text-xs font-bold hover:bg-blue-200 transition">
            <Database size={14} /> Reset Templates
          </button>
          <button onClick={onLogout} className="flex items-center gap-2 px-4 py-2 bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400 rounded-lg text-xs font-bold hover:bg-red-200 transition">
            <LogOut size={14} /> Sign Out
          </button>
        </div>
      </div>

      <div className="flex flex-col md:flex-row gap-8">
        <aside className="w-full md:w-64 flex flex-col gap-1">
          {[
            { id: 'projects', label: 'Projects', icon: <BarChart3 size={18} /> },
            { id: 'careers', label: 'Careers', icon: <Briefcase size={18} /> },
            { id: 'carousel', label: 'Gallery', icon: <ImageIcon size={18} /> },
            { id: 'specialities', label: 'Specialities', icon: <Database size={18} /> },
            { id: 'brands', label: 'Clients', icon: <Palette size={18} /> },
            { id: 'about', label: 'About Us', icon: <HistoryIcon size={18} /> },
            { id: 'branding', label: 'Visuals', icon: <Palette size={18} /> },
            { id: 'contact', label: 'Contact Info', icon: <MessageSquare size={18} /> },
            { id: 'stats', label: 'Company Stats', icon: <Save size={18} /> },
            { id: 'leads', label: 'Inquiries', icon: <Inbox size={18} /> },
            { id: 'users', label: 'User Access', icon: <Users size={18} /> },
          ].map(tab => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id as any)}
              className={`flex items-center gap-3 px-4 py-3 rounded-xl text-sm font-bold transition-all ${
                activeTab === tab.id 
                ? 'bg-blue-700 text-white shadow-lg shadow-blue-900/20' 
                : 'text-slate-500 dark:text-slate-400 hover:bg-slate-200 dark:hover:bg-slate-800'
              }`}
            >
              {tab.icon}
              {tab.label}
              {(tab.id === 'leads' && inquiries.length > 0) ? (
                <span className="ml-auto bg-red-500 text-white text-[9px] px-1.5 py-0.5 rounded-full ring-2 ring-white dark:ring-slate-950 font-black">!</span>
              ) : null}
            </button>
          ))}
        </aside>

        <main className="flex-grow bg-white dark:bg-slate-900 rounded-[2rem] p-6 md:p-10 border border-slate-200 dark:border-slate-800 shadow-sm min-h-[600px] transition-colors">
          
          {/* PROJECTS TAB */}
          {activeTab === 'projects' && (
            <div className="space-y-8">
              <div className="flex justify-between items-center">
                <h2 className="text-xl font-black text-slate-800 dark:text-white uppercase tracking-tight">Active Portfolio</h2>
                {!showProjectForm && (
                  <button onClick={startAddProject} className="bg-blue-700 text-white px-5 py-2.5 rounded-xl text-xs font-black uppercase flex items-center gap-2 hover:bg-blue-600 transition shadow-lg shadow-blue-900/10">
                    <Plus size={14} /> New Project
                  </button>
                )}
              </div>

              {showProjectForm ? (
                <form onSubmit={saveProject} className="bg-slate-50 dark:bg-slate-800/40 p-8 rounded-[2rem] border border-slate-200 dark:border-slate-700 space-y-6 animate-in fade-in slide-in-from-top-4">
                  <div className="flex justify-between items-center mb-4">
                    <h3 className="font-black text-slate-400 uppercase text-[10px] tracking-[0.2em]">{editingProjectId ? 'Modifying Project' : 'New Project Deployment'}</h3>
                    <button type="button" onClick={() => setShowProjectForm(false)} className="text-slate-400 hover:text-slate-600 transition-colors"><X size={20} /></button>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div className="space-y-1.5">
                      <label className="text-[10px] font-black uppercase text-slate-400 tracking-wider ml-1">Title</label>
                      <input required className="w-full bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-700 rounded-xl px-4 py-3 text-sm focus:ring-2 focus:ring-blue-500 outline-none transition-all dark:text-white" value={projectFormData.title} onChange={e => setProjectFormData({...projectFormData, title: e.target.value})} placeholder="e.g. Highway Expansion A1" />
                    </div>
                    <div className="space-y-1.5">
                      <label className="text-[10px] font-black uppercase text-slate-400 tracking-wider ml-1">Location</label>
                      <input required className="w-full bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-700 rounded-xl px-4 py-3 text-sm focus:ring-2 focus:ring-blue-500 outline-none transition-all dark:text-white" value={projectFormData.location} onChange={e => setProjectFormData({...projectFormData, location: e.target.value})} placeholder="e.g. Springfield, OH" />
                    </div>
                  </div>
                  
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div className="space-y-1.5">
                      <label className="text-[10px] font-black uppercase text-slate-400 tracking-wider ml-1">Operational Status</label>
                      <select className="w-full bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-700 rounded-xl px-4 py-3 text-sm focus:ring-2 focus:ring-blue-500 outline-none transition-all dark:text-white" value={projectFormData.status} onChange={e => setProjectFormData({...projectFormData, status: e.target.value as ProjectStatus})}>
                        <option value={ProjectStatus.ONGOING}>Ongoing Construction</option>
                        <option value={ProjectStatus.COMPLETED}>Fully Completed</option>
                      </select>
                    </div>
                    <div className="space-y-1.5">
                      <label className="text-[10px] font-black uppercase text-slate-400 tracking-wider ml-1">Visual Asset (Image)</label>
                      <div className="flex gap-3">
                        <input type="file" accept="image/*" className="hidden" id="p-img" onChange={async e => {
                          if (e.target.files?.[0]) {
                            const b64 = await processImage(e.target.files[0]);
                            setProjectFormData({...projectFormData, imageUrl: b64});
                          }
                        }} />
                        <label htmlFor="p-img" className="flex-grow bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-700 rounded-xl px-4 py-3 text-sm cursor-pointer flex items-center justify-between text-slate-400 hover:border-blue-500 transition-colors">
                          {projectFormData.imageUrl ? 'Photo Selected' : 'Upload Field Photo'}
                          <Upload size={16} />
                        </label>
                        {projectFormData.imageUrl && <img src={projectFormData.imageUrl} className="w-11 h-11 object-cover rounded-lg border dark:border-slate-700" />}
                      </div>
                    </div>
                  </div>

                  <div className="space-y-4">
                    <div className="flex justify-between items-center">
                      <label className="text-[10px] font-black uppercase text-slate-400 tracking-wider ml-1">Project Statistics (Custom Details)</label>
                      <button type="button" onClick={addStatRow} className="text-blue-600 hover:text-blue-500 text-[10px] font-black uppercase flex items-center gap-1">
                        <Plus size={12} /> Add Detail
                      </button>
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      {projectFormData.stats.map((stat, index) => (
                        <div key={index} className="flex gap-2 items-end bg-white dark:bg-slate-900 p-3 rounded-xl border border-slate-200 dark:border-slate-700">
                          <div className="flex-grow space-y-1">
                            <label className="text-[8px] font-black uppercase text-slate-400 tracking-widest">Label</label>
                            <input className="w-full bg-slate-50 dark:bg-slate-800 border-none rounded-lg px-3 py-1.5 text-xs dark:text-white outline-none" value={stat.label} onChange={e => updateStatRow(index, 'label', e.target.value)} placeholder="e.g. Length" />
                          </div>
                          <div className="flex-grow space-y-1">
                            <label className="text-[8px] font-black uppercase text-slate-400 tracking-widest">Value</label>
                            <input className="w-full bg-slate-50 dark:bg-slate-800 border-none rounded-lg px-3 py-1.5 text-xs dark:text-white outline-none" value={stat.value} onChange={e => updateStatRow(index, 'value', e.target.value)} placeholder="e.g. 50km" />
                          </div>
                          <button type="button" onClick={() => removeStatRow(index)} className="p-2 text-red-500 hover:bg-red-50 dark:hover:bg-red-900/30 rounded-lg transition-colors mb-0.5">
                            <Trash2 size={14} />
                          </button>
                        </div>
                      ))}
                    </div>
                  </div>

                  <div className="space-y-1.5">
                    <label className="text-[10px] font-black uppercase text-slate-400 tracking-wider ml-1">Technical Briefing</label>
                    <textarea required rows={4} className="w-full bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-700 rounded-xl px-4 py-3 text-sm focus:ring-2 focus:ring-blue-500 outline-none transition-all dark:text-white" value={projectFormData.description} onChange={e => setProjectFormData({...projectFormData, description: e.target.value})} placeholder="Technical specifics, materials used, challenges overcome..." />
                  </div>

                  <div className="flex gap-4 pt-2">
                    <button type="submit" disabled={isProcessing} className="bg-blue-700 text-white px-8 py-3 rounded-xl text-xs font-black uppercase hover:bg-blue-600 transition shadow-xl shadow-blue-900/10 active:scale-95 disabled:opacity-50">
                      {isProcessing ? 'Synchronizing...' : (editingProjectId ? 'Apply Changes' : 'Deploy Project')}
                    </button>
                    <button type="button" onClick={() => setShowProjectForm(false)} className="bg-slate-200 dark:bg-slate-800 text-slate-700 dark:text-slate-300 px-8 py-3 rounded-xl text-xs font-black uppercase hover:bg-slate-300 dark:hover:bg-slate-700 transition">Cancel</button>
                  </div>
                </form>
              ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                  {projects.map(p => (
                    <div key={p.id} className="group bg-slate-50 dark:bg-slate-800/30 border border-slate-200 dark:border-slate-700 rounded-2xl overflow-hidden p-4 relative hover:border-blue-500 transition-all duration-300">
                      <div className="aspect-video bg-slate-200 dark:bg-slate-800 rounded-xl mb-4 overflow-hidden relative">
                        <img src={p.imageUrl} alt={p.title} className="w-full h-full object-cover" />
                        <div className={`absolute top-2 left-2 px-2 py-0.5 rounded-full text-[8px] font-black uppercase text-white shadow-lg ${p.status === ProjectStatus.COMPLETED ? 'bg-green-500' : 'bg-blue-600'}`}>
                          {p.status}
                        </div>
                      </div>
                      <div className="px-1">
                        <h4 className="font-bold text-slate-900 dark:text-white truncate text-sm">{p.title}</h4>
                        <p className="text-[9px] text-slate-400 uppercase font-black tracking-widest">{p.location}</p>
                      </div>
                      
                      <div className="absolute top-2 right-2 flex gap-1 opacity-0 group-hover:opacity-100 transition-all scale-95 group-hover:scale-100">
                        <button onClick={() => startEditProject(p)} className="p-2 bg-white dark:bg-slate-900 text-blue-600 rounded-lg shadow-xl hover:text-blue-500 transition-colors">
                          <Edit3 size={14} />
                        </button>
                        <button onClick={() => {
                          triggerConfirm(
                            "Archive Project?",
                            "Are you sure you want to permanently archive this project record?",
                            async () => {
                              setIsProcessing(true);
                              try {
                                await dbService.deleteProject(p.id);
                                onUpdateProjects(projects.filter(pr => pr.id !== p.id));
                                showNotification("Project archived.", "success");
                              } catch (e) {
                                console.error("Delete Project Error:", e);
                                showNotification("Failed to delete project.", "error");
                              } finally {
                                setIsProcessing(false);
                              }
                            }
                          );
                        }} className="p-2 bg-white dark:bg-slate-900 text-red-600 rounded-lg shadow-xl hover:text-red-500 transition-colors">
                          <Trash2 size={14} />
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          {/* CAREERS TAB */}
          {activeTab === 'careers' && (
            <div className="space-y-8">
              <div className="flex justify-between items-center">
                <h2 className="text-xl font-black text-slate-800 dark:text-white uppercase tracking-tight">Human Resources</h2>
                {!showCareerForm && (
                  <button onClick={startAddCareer} className="bg-blue-700 text-white px-5 py-2.5 rounded-xl text-xs font-black uppercase flex items-center gap-2 shadow-lg shadow-blue-900/10">
                    <Plus size={14} /> Post Opening
                  </button>
                )}
              </div>

              {showCareerForm ? (
                <form onSubmit={saveCareer} className="bg-slate-50 dark:bg-slate-800/40 p-8 rounded-[2rem] border border-slate-200 dark:border-slate-700 space-y-6 animate-in fade-in slide-in-from-top-4">
                   <div className="flex justify-between items-center mb-4">
                    <h3 className="font-black text-slate-400 uppercase text-[10px] tracking-[0.2em]">{editingCareerId ? 'Modifying Job Listing' : 'New Personnel Requirement'}</h3>
                    <button type="button" onClick={() => setShowCareerForm(false)} className="text-slate-400 hover:text-slate-600 transition-colors"><X size={20} /></button>
                  </div>
                  
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div className="space-y-1.5">
                      <label className="text-[10px] font-black uppercase text-slate-400 tracking-wider ml-1">Role Title</label>
                      <input required className="w-full bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-700 rounded-xl px-4 py-3 text-sm dark:text-white outline-none focus:ring-2 focus:ring-blue-500" value={careerFormData.title} onChange={e => setCareerFormData({...careerFormData, title: e.target.value})} placeholder="e.g. Senior Asphalt Specialist" />
                    </div>
                    <div className="space-y-1.5">
                      <label className="text-[10px] font-black uppercase text-slate-400 tracking-wider ml-1">Deployment Location</label>
                      <input required className="w-full bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-700 rounded-xl px-4 py-3 text-sm dark:text-white outline-none focus:ring-2 focus:ring-blue-500" value={careerFormData.location} onChange={e => setCareerFormData({...careerFormData, location: e.target.value})} placeholder="e.g. Miami Base" />
                    </div>
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div className="space-y-1.5">
                      <label className="text-[10px] font-black uppercase text-slate-400 tracking-wider ml-1">Contract Type</label>
                      <select className="w-full bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-700 rounded-xl px-4 py-3 text-sm dark:text-white outline-none focus:ring-2 focus:ring-blue-500" value={careerFormData.type} onChange={e => setCareerFormData({...careerFormData, type: e.target.value as any})}>
                        <option>Full-time</option>
                        <option>Contract</option>
                      </select>
                    </div>
                    <div className="space-y-1.5">
                      <label className="text-[10px] font-black uppercase text-slate-400 tracking-wider ml-1">Division</label>
                      <input required className="w-full bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-700 rounded-xl px-4 py-3 text-sm dark:text-white outline-none focus:ring-2 focus:ring-blue-500" value={careerFormData.department} onChange={e => setCareerFormData({...careerFormData, department: e.target.value})} placeholder="e.g. Earthworks Operations" />
                    </div>
                  </div>
                  <div className="space-y-1.5">
                    <label className="text-[10px] font-black uppercase text-slate-400 tracking-wider ml-1">Full Description & Requirements</label>
                    <textarea required rows={5} className="w-full bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-700 rounded-xl px-4 py-3 text-sm dark:text-white outline-none focus:ring-2 focus:ring-blue-500" value={careerFormData.description} onChange={e => setCareerFormData({...careerFormData, description: e.target.value})} placeholder="Outline duties, necessary certifications, and experience levels..." />
                  </div>
                  <div className="flex gap-4 pt-2">
                    <button type="submit" className="bg-blue-700 text-white px-8 py-3 rounded-xl text-xs font-black uppercase hover:bg-blue-600 transition shadow-xl shadow-blue-900/10 active:scale-95">Sync Recruitment Data</button>
                    <button type="button" onClick={() => setShowCareerForm(false)} className="bg-slate-200 dark:bg-slate-800 text-slate-700 dark:text-slate-300 px-8 py-3 rounded-xl text-xs font-black uppercase">Cancel</button>
                  </div>
                </form>
              ) : (
                <div className="space-y-3">
                  {careers.length === 0 ? (
                    <div className="text-center py-12 text-slate-400 font-bold uppercase text-[10px] tracking-widest border border-dashed rounded-2xl">No Active Recruitment</div>
                  ) : careers.map(c => (
                    <div key={c.id} className="bg-slate-50 dark:bg-slate-800/30 p-5 rounded-2xl border border-slate-200 dark:border-slate-700 flex justify-between items-center group hover:border-blue-500 transition-colors">
                      <div className="min-w-0">
                        <h4 className="font-bold text-slate-900 dark:text-white truncate">{c.title}</h4>
                        <p className="text-[9px] text-slate-400 font-black uppercase tracking-widest flex items-center gap-2">
                          <span className="text-blue-600">{c.department}</span> • {c.location} • {c.type}
                        </p>
                      </div>
                      <div className="flex gap-1">
                        <button onClick={() => startEditCareer(c)} className="p-2 text-blue-600 hover:bg-blue-50 dark:hover:bg-blue-900/30 rounded-lg transition-colors">
                          <Edit3 size={16} />
                        </button>
                        <button onClick={() => {
                          triggerConfirm(
                            "Remove Vacancy?",
                            "Are you sure you want to remove this vacancy from active recruitment?",
                            async () => {
                              setIsProcessing(true);
                              try {
                                await dbService.deleteCareer(c.id);
                                onUpdateCareers(careers.filter(cr => cr.id !== c.id));
                                showNotification("Career listing removed.", "success");
                              } catch (e) {
                                console.error("Delete Career Error:", e);
                                showNotification("Failed to delete career.", "error");
                              } finally {
                                setIsProcessing(false);
                              }
                            }
                          );
                        }} className="p-2 text-red-600 hover:bg-red-50 dark:hover:bg-red-900/30 rounded-lg transition-colors">
                          <Trash2 size={16} />
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          {/* CLIENTS TAB */}
          {activeTab === 'brands' && (
            <div className="space-y-8">
              <div className="flex justify-between items-center">
                <h2 className="text-xl font-black text-slate-800 dark:text-white uppercase tracking-tight">Our Clients & Partners</h2>
              </div>

              <div className="bg-slate-50 dark:bg-slate-800/40 p-8 rounded-[2rem] border border-slate-200 dark:border-slate-700 space-y-6">
                <div className="flex justify-between items-center">
                  <h3 className="text-[10px] font-black uppercase text-slate-400 tracking-[0.2em]">{editingBrandId ? 'Edit Client Details' : 'Add New Client Logo'}</h3>
                  {editingBrandId && (
                    <button 
                      onClick={() => {
                        setEditingBrandId(null);
                        setBrandUpload({ name: '', abbreviation: '', imageUrl: '' });
                      }}
                      className="text-red-500 text-[10px] font-black uppercase tracking-wider hover:underline"
                    >
                      Cancel Edit
                    </button>
                  )}
                </div>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                  <div className="space-y-1.5">
                    <label className="text-[10px] font-black uppercase text-slate-400 tracking-wider ml-1">Client Name</label>
                    <input 
                      className="w-full bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-700 rounded-xl px-4 py-3 text-sm dark:text-white outline-none focus:ring-2 focus:ring-blue-500" 
                      value={brandUpload.name} 
                      onChange={e => setBrandUpload({...brandUpload, name: e.target.value})} 
                      placeholder="e.g. Caterpillar Inc." 
                    />
                  </div>
                  <div className="space-y-1.5">
                    <label className="text-[10px] font-black uppercase text-slate-400 tracking-wider ml-1">Abbreviation (Optional)</label>
                    <input 
                      className="w-full bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-700 rounded-xl px-4 py-3 text-sm dark:text-white outline-none focus:ring-2 focus:ring-blue-500" 
                      value={brandUpload.abbreviation} 
                      onChange={e => setBrandUpload({...brandUpload, abbreviation: e.target.value})} 
                      placeholder="e.g. CAT" 
                    />
                  </div>
                  <div className="space-y-1.5">
                    <label className="text-[10px] font-black uppercase text-slate-400 tracking-wider ml-1">Logo Upload</label>
                    <div className="flex gap-3">
                      <input type="file" accept="image/*" className="hidden" id="brand-img" onChange={async e => {
                        if (e.target.files?.[0]) {
                          const b64 = await processImage(e.target.files[0]);
                          setBrandUpload({...brandUpload, imageUrl: b64});
                        }
                      }} />
                      <label htmlFor="brand-img" className="flex-grow bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-700 rounded-xl px-4 py-3 text-sm cursor-pointer flex items-center justify-between text-slate-400 hover:border-blue-500 transition-colors">
                        {brandUpload.imageUrl ? 'Logo Selected' : 'Upload PNG/SVG Logo'}
                        <Upload size={16} />
                      </label>
                      {brandUpload.imageUrl && <img src={brandUpload.imageUrl} className="w-11 h-11 object-contain bg-white rounded-lg border dark:border-slate-700 p-1" />}
                    </div>
                  </div>
                </div>
                <button 
                  onClick={saveBrand} 
                  disabled={isProcessing}
                  className="bg-blue-700 text-white px-8 py-3 rounded-xl text-xs font-black uppercase hover:bg-blue-600 transition shadow-xl shadow-blue-900/10 active:scale-95 disabled:opacity-50"
                >
                  {isProcessing ? 'Processing...' : editingBrandId ? 'Update Client' : 'Add Client Logo'}
                </button>
              </div>

              <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-4">
                {allBrands.length === 0 ? (
                  <div className="col-span-full text-center py-12 text-slate-400 font-bold uppercase text-[10px] tracking-widest border border-dashed rounded-2xl">
                    No client logos uploaded
                  </div>
                ) : allBrands.map(b => (
                  <div key={b.id} className="group relative bg-slate-50 dark:bg-slate-800/50 p-3 rounded-xl border border-slate-200 dark:border-slate-700 flex flex-col items-center justify-center gap-2 hover:border-blue-500 transition-all">
                    <div className="h-16 w-full flex items-center justify-center bg-white rounded-lg p-2">
                      <img src={b.imageUrl} alt={b.name} className="max-h-full max-w-full object-contain" />
                    </div>
                    <p className="text-[9px] font-black uppercase text-slate-400 tracking-tight text-center truncate w-full">{b.name}</p>
                    <div className="absolute -top-2 -right-2 flex gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                      <button 
                        onClick={() => startEditBrand(b)}
                        className="p-1.5 bg-blue-600 text-white rounded-full shadow-lg hover:bg-blue-500 transition-colors"
                      >
                        <Edit3 size={10} />
                      </button>
                      <button 
                        onClick={() => {
                          triggerConfirm(
                            "Remove Client?",
                            "Are you sure you want to remove this client logo?",
                            async () => {
                              setIsProcessing(true);
                              try {
                                await dbService.deleteBrand(b.id);
                                const updated = allBrands.filter(brand => brand.id !== b.id);
                                setAllBrands(updated);
                                onUpdateBrands(updated);
                                showNotification("Client removed.", "success");
                              } catch (e) {
                                console.error("Delete Client Error:", e);
                                showNotification("Failed to delete client.", "error");
                              } finally {
                                setIsProcessing(false);
                              }
                            }
                          );
                        }}
                        className="p-1.5 bg-red-500 text-white rounded-full shadow-lg hover:bg-red-400 transition-colors"
                      >
                        <Trash2 size={10} />
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* ABOUT US TAB */}
          {activeTab === 'about' && (
            <div className="space-y-10">
              <h2 className="text-xl font-black text-slate-800 dark:text-white uppercase tracking-tight">About Us Content Management</h2>
              
              <div className="space-y-12">
                {/* Company Profile */}
                <div className="p-8 bg-slate-50 dark:bg-slate-800/40 rounded-[2.5rem] border border-slate-200 dark:border-slate-700 space-y-6">
                  <h3 className="text-sm font-black uppercase text-blue-600 dark:text-blue-400 tracking-widest">Company Profile Section</h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div className="space-y-4">
                      <div className="space-y-1.5">
                        <label className="text-[10px] font-black uppercase text-slate-400 tracking-wider ml-1">Profile Title</label>
                        <input className="w-full bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-700 rounded-xl px-4 py-3 text-sm dark:text-white outline-none focus:ring-2 focus:ring-blue-500" value={aboutForm.profileTitle} onChange={e => setAboutForm({...aboutForm, profileTitle: e.target.value})} />
                      </div>
                      <div className="space-y-1.5">
                        <label className="text-[10px] font-black uppercase text-slate-400 tracking-wider ml-1">Profile Description</label>
                        <textarea rows={4} className="w-full bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-700 rounded-xl px-4 py-3 text-sm dark:text-white outline-none focus:ring-2 focus:ring-blue-500" value={aboutForm.profileDescription} onChange={e => setAboutForm({...aboutForm, profileDescription: e.target.value})} />
                      </div>
                    </div>
                    <div className="space-y-1.5">
                      <label className="text-[10px] font-black uppercase text-slate-400 tracking-wider ml-1">Profile Image</label>
                      <div className="flex flex-col gap-4">
                        <div className="aspect-video bg-white dark:bg-slate-950 rounded-2xl overflow-hidden border border-dashed border-slate-300 dark:border-slate-700">
                          <img src={aboutForm.profileImage} className="w-full h-full object-cover" />
                        </div>
                        <input type="file" id="profile-img" className="hidden" onChange={async e => {
                          if (e.target.files?.[0]) {
                            const b64 = await processImage(e.target.files[0]);
                            setAboutForm({...aboutForm, profileImage: b64});
                          }
                        }} />
                        <label htmlFor="profile-img" className="bg-blue-700 text-white px-6 py-2.5 rounded-xl text-xs font-black uppercase cursor-pointer hover:bg-blue-600 text-center">Replace Image</label>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Foundation */}
                <div className="p-8 bg-slate-50 dark:bg-slate-800/40 rounded-[2.5rem] border border-slate-200 dark:border-slate-700 space-y-6">
                  <h3 className="text-sm font-black uppercase text-blue-600 dark:text-blue-400 tracking-widest">Foundation Section</h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div className="space-y-4">
                      <div className="space-y-1.5">
                        <label className="text-[10px] font-black uppercase text-slate-400 tracking-wider ml-1">Foundation Title</label>
                        <input className="w-full bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-700 rounded-xl px-4 py-3 text-sm dark:text-white outline-none focus:ring-2 focus:ring-blue-500" value={aboutForm.foundationTitle} onChange={e => setAboutForm({...aboutForm, foundationTitle: e.target.value})} />
                      </div>
                      <div className="space-y-1.5">
                        <label className="text-[10px] font-black uppercase text-slate-400 tracking-wider ml-1">Foundation Description</label>
                        <textarea rows={4} className="w-full bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-700 rounded-xl px-4 py-3 text-sm dark:text-white outline-none focus:ring-2 focus:ring-blue-500" value={aboutForm.foundationDescription} onChange={e => setAboutForm({...aboutForm, foundationDescription: e.target.value})} />
                      </div>
                    </div>
                    <div className="space-y-1.5">
                      <label className="text-[10px] font-black uppercase text-slate-400 tracking-wider ml-1">Foundation Image</label>
                      <div className="flex flex-col gap-4">
                        <div className="aspect-square bg-white dark:bg-slate-950 rounded-2xl overflow-hidden border border-dashed border-slate-300 dark:border-slate-700">
                          <img src={aboutForm.foundationImage} className="w-full h-full object-cover" />
                        </div>
                        <input type="file" id="found-img" className="hidden" onChange={async e => {
                          if (e.target.files?.[0]) {
                            const b64 = await processImage(e.target.files[0]);
                            setAboutForm({...aboutForm, foundationImage: b64});
                          }
                        }} />
                        <label htmlFor="found-img" className="bg-blue-700 text-white px-6 py-2.5 rounded-xl text-xs font-black uppercase cursor-pointer hover:bg-blue-600 text-center">Replace Image</label>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Mission & Vision */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                  <div className="p-8 bg-slate-50 dark:bg-slate-800/40 rounded-[2.5rem] border border-slate-200 dark:border-slate-700 space-y-6">
                    <h3 className="text-sm font-black uppercase text-blue-600 dark:text-blue-400 tracking-widest">Mission</h3>
                    <div className="space-y-4">
                      <div className="space-y-1.5">
                        <label className="text-[10px] font-black uppercase text-slate-400 tracking-wider ml-1">Mission Title</label>
                        <input className="w-full bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-700 rounded-xl px-4 py-3 text-sm dark:text-white outline-none focus:ring-2 focus:ring-blue-500" value={aboutForm.missionTitle} onChange={e => setAboutForm({...aboutForm, missionTitle: e.target.value})} />
                      </div>
                      <div className="space-y-1.5">
                        <label className="text-[10px] font-black uppercase text-slate-400 tracking-wider ml-1">Mission Description</label>
                        <textarea rows={3} className="w-full bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-700 rounded-xl px-4 py-3 text-sm dark:text-white outline-none focus:ring-2 focus:ring-blue-500" value={aboutForm.missionDescription} onChange={e => setAboutForm({...aboutForm, missionDescription: e.target.value})} />
                      </div>
                      <div className="space-y-1.5">
                        <label className="text-[10px] font-black uppercase text-slate-400 tracking-wider ml-1">Mission Points (Comma separated)</label>
                        <input className="w-full bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-700 rounded-xl px-4 py-3 text-sm dark:text-white outline-none focus:ring-2 focus:ring-blue-500" value={aboutForm.missionPoints.join(', ')} onChange={e => setAboutForm({...aboutForm, missionPoints: e.target.value.split(',').map(p => p.trim())})} />
                      </div>
                    </div>
                  </div>
                  <div className="p-8 bg-slate-50 dark:bg-slate-800/40 rounded-[2.5rem] border border-slate-200 dark:border-slate-700 space-y-6">
                    <h3 className="text-sm font-black uppercase text-blue-600 dark:text-blue-400 tracking-widest">Vision</h3>
                    <div className="space-y-4">
                      <div className="space-y-1.5">
                        <label className="text-[10px] font-black uppercase text-slate-400 tracking-wider ml-1">Vision Title</label>
                        <input className="w-full bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-700 rounded-xl px-4 py-3 text-sm dark:text-white outline-none focus:ring-2 focus:ring-blue-500" value={aboutForm.visionTitle} onChange={e => setAboutForm({...aboutForm, visionTitle: e.target.value})} />
                      </div>
                      <div className="space-y-1.5">
                        <label className="text-[10px] font-black uppercase text-slate-400 tracking-wider ml-1">Vision Description</label>
                        <textarea rows={3} className="w-full bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-700 rounded-xl px-4 py-3 text-sm dark:text-white outline-none focus:ring-2 focus:ring-blue-500" value={aboutForm.visionDescription} onChange={e => setAboutForm({...aboutForm, visionDescription: e.target.value})} />
                      </div>
                      <div className="space-y-1.5">
                        <label className="text-[10px] font-black uppercase text-slate-400 tracking-wider ml-1">Vision Points (Comma separated)</label>
                        <input className="w-full bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-700 rounded-xl px-4 py-3 text-sm dark:text-white outline-none focus:ring-2 focus:ring-blue-500" value={aboutForm.visionPoints.join(', ')} onChange={e => setAboutForm({...aboutForm, visionPoints: e.target.value.split(',').map(p => p.trim())})} />
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              <div className="flex justify-end pt-6 border-t dark:border-slate-800">
                <button onClick={saveAbout} className="bg-green-600 text-white px-12 py-4 rounded-2xl font-black uppercase tracking-[0.2em] text-sm hover:bg-green-500 shadow-2xl shadow-green-900/20 active:scale-95 transition-all">Sync About Us Content</button>
              </div>
            </div>
          )}

          {/* VISUALS / BRANDING TAB */}
          {activeTab === 'branding' && (
            <div className="space-y-10">
              <h2 className="text-xl font-black text-slate-800 dark:text-white uppercase tracking-tight">Identity Visuals</h2>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-10">
                <div className="space-y-4">
                  <div className="p-8 bg-slate-50 dark:bg-slate-800/40 rounded-[2.5rem] border border-slate-200 dark:border-slate-700">
                    <label className="text-[10px] font-black uppercase text-slate-400 block mb-4 tracking-[0.2em]">Corporate Logo</label>
                    <div className="flex flex-col items-center gap-6">
                      <div className="h-28 w-full bg-white dark:bg-slate-950 border border-dashed border-slate-300 dark:border-slate-700 rounded-2xl flex items-center justify-center p-6 transition-all group hover:border-blue-500">
                        {brandingForm.logoUrl ? <img src={brandingForm.logoUrl} className="max-h-full max-w-full object-contain" /> : <div className="text-slate-400 text-xs italic font-medium flex items-center gap-2"><ImageIcon size={14} /> Global Default Active</div>}
                      </div>
                      <input type="file" id="logo-up" className="hidden" onChange={async e => {
                        if (e.target.files?.[0]) {
                          const b64 = await processImage(e.target.files[0]);
                          setBrandingForm({...brandingForm, logoUrl: b64});
                        }
                      }} />
                      <label htmlFor="logo-up" className="bg-blue-700 text-white px-8 py-3 rounded-xl text-xs font-black uppercase cursor-pointer hover:bg-blue-600 shadow-lg shadow-blue-900/10 active:scale-95 transition-all">Replace Global Logo</label>
                    </div>
                  </div>
                </div>

                <div className="space-y-4">
                  <div className="p-8 bg-slate-50 dark:bg-slate-800/40 rounded-[2.5rem] border border-slate-200 dark:border-slate-700">
                    <label className="text-[10px] font-black uppercase text-slate-400 block mb-4 tracking-[0.2em]">Hero Background Asset</label>
                    <div className="flex flex-col items-center gap-6">
                      <div className="aspect-[21/9] w-full bg-white dark:bg-slate-950 rounded-2xl overflow-hidden border border-dashed border-slate-300 dark:border-slate-700 transition-all group hover:border-blue-500">
                        {brandingForm.heroImageUrl ? <img src={brandingForm.heroImageUrl} className="w-full h-full object-cover" /> : <div className="h-full flex items-center justify-center text-slate-400 italic font-medium gap-2"><ImageIcon size={14} /> Unsplash High-res Active</div>}
                      </div>
                      <input type="file" id="hero-up" className="hidden" onChange={async e => {
                        if (e.target.files?.[0]) {
                          const b64 = await processImage(e.target.files[0]);
                          setBrandingForm({...brandingForm, heroImageUrl: b64});
                        }
                      }} />
                      <label htmlFor="hero-up" className="bg-blue-700 text-white px-8 py-3 rounded-xl text-xs font-black uppercase cursor-pointer hover:bg-blue-600 shadow-lg shadow-blue-900/10 active:scale-95 transition-all">Replace Hero Asset</label>
                    </div>
                  </div>
                </div>
              </div>

              <div className="flex justify-end pt-6 border-t dark:border-slate-800">
                <button onClick={saveBranding} className="bg-green-600 text-white px-12 py-4 rounded-2xl font-black uppercase tracking-[0.2em] text-sm hover:bg-green-500 shadow-2xl shadow-green-900/20 active:scale-95 transition-all">Sync Visual Identity</button>
              </div>
            </div>
          )}

          {/* STATS TAB */}
          {activeTab === 'stats' && (
            <div className="space-y-8">
              <h2 className="text-xl font-black text-slate-800 dark:text-white uppercase tracking-tight">Performance Counters</h2>
              <div className="p-10 bg-slate-50 dark:bg-slate-800/40 rounded-[2.5rem] border border-slate-200 dark:border-slate-700 max-w-2xl">
                <div className="space-y-8">
                  <div className="space-y-2">
                    <label className="text-[10px] font-black uppercase text-slate-400 tracking-[0.2em] ml-1">Engineering Tenure (Years)</label>
                    <input type="number" className="w-full bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-700 rounded-xl px-5 py-4 text-xl font-black dark:text-white focus:ring-2 focus:ring-blue-500 outline-none" value={statsForm.yearsExperience} onChange={e => setStatsForm({...statsForm, yearsExperience: parseInt(e.target.value) || 0})} />
                  </div>
                  <div className="space-y-2">
                    <label className="text-[10px] font-black uppercase text-slate-400 tracking-[0.2em] ml-1">Grand Total of Projects</label>
                    <input type="number" className="w-full bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-700 rounded-xl px-5 py-4 text-xl font-black dark:text-white focus:ring-2 focus:ring-blue-500 outline-none" value={statsForm.projectsCompleted} onChange={e => setStatsForm({...statsForm, projectsCompleted: parseInt(e.target.value) || 0})} />
                  </div>
                  <div className="space-y-2">
                    <label className="text-[10px] font-black uppercase text-slate-400 tracking-[0.2em] ml-1">Infrastructure Footprint (KMs)</label>
                    <input type="text" className="w-full bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-700 rounded-xl px-5 py-4 text-xl font-black dark:text-white focus:ring-2 focus:ring-blue-500 outline-none" value={statsForm.kilometersBuilt} onChange={e => setStatsForm({...statsForm, kilometersBuilt: e.target.value})} placeholder="e.g. 15,000+" />
                  </div>
                  <button onClick={saveStats} className="w-full bg-blue-700 text-white py-5 rounded-2xl font-black uppercase tracking-[0.2em] hover:bg-blue-600 transition-all shadow-2xl shadow-blue-900/20 active:scale-95 text-sm">Deploy Performance Metrics</button>
                </div>
              </div>
            </div>
          )}

          {/* GALLERY TAB */}
          {activeTab === 'carousel' && (
            <div className="space-y-10">
              <h2 className="text-xl font-black text-slate-800 dark:text-white uppercase tracking-tight">Highlight Gallery Assets</h2>

              <div className="p-8 bg-slate-50 dark:bg-slate-800/40 rounded-[2.5rem] border border-slate-200 dark:border-slate-700">
                <h4 className="text-[10px] font-black uppercase text-slate-400 mb-6 tracking-[0.2em]">Add Immersive Field Capture</h4>
                <div className="flex flex-col md:flex-row gap-6 items-end">
                  <div className="flex-grow space-y-2">
                    <input type="file" id="car-up" className="hidden" onChange={async e => {
                      if (e.target.files?.[0]) {
                        const b64 = await processImage(e.target.files[0]);
                        setCarouselUpload({...carouselUpload, imageUrl: b64});
                      }
                    }} />
                    <label htmlFor="car-up" className="w-full h-14 bg-white dark:bg-slate-900 border border-dashed border-slate-300 dark:border-slate-700 rounded-xl flex items-center justify-between px-5 text-xs font-black text-slate-400 cursor-pointer hover:border-blue-500 transition-all">
                      {carouselUpload.imageUrl ? 'Photo Processed Successfully' : 'Select Landscape Asset'}
                      <Upload size={16} />
                    </label>
                  </div>
                  <div className="flex-grow space-y-2">
                    <input className="w-full h-14 bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-700 rounded-xl px-5 text-xs font-black dark:text-white focus:ring-2 focus:ring-blue-500 outline-none transition-all" placeholder="Alt text / Description..." value={carouselUpload.description} onChange={e => setCarouselUpload({...carouselUpload, description: e.target.value})} />
                  </div>
                  <button onClick={async () => {
                    if (!carouselUpload.imageUrl) {
                      showNotification("Please select an image asset.", "error");
                      return;
                    }
                    setIsProcessing(true);
                    try {
                      const newItem = { id: generateId(), ...carouselUpload };
                      await dbService.saveCarousel(newItem);
                      onUpdateCarousel([...carouselItems, newItem]);
                      setCarouselUpload({ imageUrl: '', description: '' });
                      showNotification("Gallery asset added!", "success");
                    } catch (error: any) {
                      console.error("Gallery Upload Error:", error);
                      if (error.message?.includes('longer than 1048487 bytes')) {
                        showNotification("Image too large. Max 1MB.", "error");
                      } else {
                        showNotification("Upload failed.", "error");
                      }
                    } finally {
                      setIsProcessing(false);
                    }
                  }} className="h-14 bg-blue-700 text-white px-10 rounded-xl font-black uppercase text-[10px] whitespace-nowrap shadow-xl shadow-blue-900/10 active:scale-95 transition-all">Inject to Gallery</button>
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
                {carouselItems.map(item => (
                  <div key={item.id} className="relative group rounded-[2rem] overflow-hidden border border-slate-200 dark:border-slate-800 shadow-sm transition-all hover:border-blue-500">
                    <img src={item.imageUrl} className="aspect-[16/9] w-full object-cover" />
                    <div className="absolute inset-0 bg-slate-900/80 backdrop-blur-sm opacity-0 group-hover:opacity-100 transition-opacity flex flex-col justify-center items-center p-8 text-center">
                      <p className="text-white text-[10px] font-black uppercase tracking-widest mb-6 leading-relaxed">"{item.description}"</p>
                      <button onClick={() => {
                        triggerConfirm(
                          "Remove Asset?",
                          "Are you sure you want to remove this asset from the global gallery?",
                          async () => {
                            setIsProcessing(true);
                            try {
                              await dbService.deleteCarousel(item.id);
                              onUpdateCarousel(carouselItems.filter(i => i.id !== item.id));
                              showNotification("Asset removed.", "success");
                            } catch (e) {
                              console.error("Delete Carousel Error:", e);
                              showNotification("Failed to delete asset.", "error");
                            } finally {
                              setIsProcessing(false);
                            }
                          }
                        );
                      }} className="p-4 bg-red-600 text-white rounded-2xl hover:bg-red-500 shadow-2xl shadow-red-900/40 active:scale-90 transition-all">
                        <Trash2 size={24} />
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* SPECIALITIES TAB */}
          {activeTab === 'specialities' && (
            <div className="space-y-8">
              <div className="flex justify-between items-center">
                <h2 className="text-xl font-black text-slate-800 dark:text-white uppercase tracking-tight">Infrastructure Specialities</h2>
                {!showSpecialityForm && (
                  <button onClick={startAddSpeciality} className="bg-blue-700 text-white px-5 py-2.5 rounded-xl text-xs font-black uppercase flex items-center gap-2 hover:bg-blue-600 transition shadow-lg shadow-blue-900/10">
                    <Plus size={14} /> New Speciality
                  </button>
                )}
              </div>

              {showSpecialityForm ? (
                <form onSubmit={saveSpeciality} className="bg-slate-50 dark:bg-slate-800/40 p-8 rounded-[2rem] border border-slate-200 dark:border-slate-700 space-y-6 animate-in fade-in slide-in-from-top-4">
                  <div className="flex justify-between items-center mb-4">
                    <h3 className="font-black text-slate-400 uppercase text-[10px] tracking-[0.2em]">{editingSpecialityId ? 'Modifying Speciality' : 'New Speciality Deployment'}</h3>
                    <button type="button" onClick={() => setShowSpecialityForm(false)} className="text-slate-400 hover:text-slate-600 transition-colors"><X size={20} /></button>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div className="space-y-1.5">
                      <label className="text-[10px] font-black uppercase text-slate-400 tracking-wider ml-1">Title</label>
                      <input required className="w-full bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-700 rounded-xl px-4 py-3 text-sm focus:ring-2 focus:ring-blue-500 outline-none transition-all dark:text-white" value={specialityFormData.title} onChange={e => setSpecialityFormData({...specialityFormData, title: e.target.value})} placeholder="e.g. Highway Construction" />
                    </div>
                    <div className="space-y-1.5">
                      <label className="text-[10px] font-black uppercase text-slate-400 tracking-wider ml-1">Visual Asset (Image)</label>
                      <div className="flex gap-3">
                        <input type="file" accept="image/*" className="hidden" id="s-img" onChange={async e => {
                          if (e.target.files?.[0]) {
                            const b64 = await processImage(e.target.files[0]);
                            setSpecialityFormData({...specialityFormData, imageUrl: b64});
                          }
                        }} />
                        <label htmlFor="s-img" className="flex-grow bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-700 rounded-xl px-4 py-3 text-sm cursor-pointer flex items-center justify-between text-slate-400 hover:border-blue-500 transition-colors">
                          {specialityFormData.imageUrl ? 'Photo Selected' : 'Upload Speciality Photo'}
                          <Upload size={16} />
                        </label>
                        {specialityFormData.imageUrl && <img src={specialityFormData.imageUrl} className="w-11 h-11 object-cover rounded-lg border dark:border-slate-700" />}
                      </div>
                    </div>
                  </div>

                  <div className="space-y-1.5">
                    <label className="text-[10px] font-black uppercase text-slate-400 tracking-wider ml-1">One-line Description</label>
                    <input required className="w-full bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-700 rounded-xl px-4 py-3 text-sm focus:ring-2 focus:ring-blue-500 outline-none transition-all dark:text-white" value={specialityFormData.description} onChange={e => setSpecialityFormData({...specialityFormData, description: e.target.value})} placeholder="Brief description of the speciality..." />
                  </div>

                  <div className="flex gap-4 pt-2">
                    <button type="submit" disabled={isProcessing} className="bg-blue-700 text-white px-8 py-3 rounded-xl text-xs font-black uppercase hover:bg-blue-600 transition shadow-xl shadow-blue-900/10 active:scale-95 disabled:opacity-50">
                      {isProcessing ? 'Synchronizing...' : (editingSpecialityId ? 'Apply Changes' : 'Deploy Speciality')}
                    </button>
                    <button type="button" onClick={() => setShowSpecialityForm(false)} className="bg-slate-200 dark:bg-slate-800 text-slate-700 dark:text-slate-300 px-8 py-3 rounded-xl text-xs font-black uppercase hover:bg-slate-300 dark:hover:bg-slate-700 transition">Cancel</button>
                  </div>
                </form>
              ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                  {specialities.map(s => (
                    <div key={s.id} className="group bg-slate-50 dark:bg-slate-800/30 border border-slate-200 dark:border-slate-700 rounded-2xl overflow-hidden p-4 relative hover:border-blue-500 transition-all duration-300">
                      <div className="aspect-video bg-slate-200 dark:bg-slate-800 rounded-xl mb-4 overflow-hidden relative">
                        <img src={s.imageUrl} alt={s.title} className="w-full h-full object-cover" />
                      </div>
                      <div className="px-1">
                        <h4 className="font-bold text-slate-900 dark:text-white truncate text-sm">{s.title}</h4>
                        <p className="text-[10px] text-slate-500 line-clamp-2">{s.description}</p>
                      </div>
                      
                      <div className="absolute top-2 right-2 flex gap-1 opacity-0 group-hover:opacity-100 transition-all scale-95 group-hover:scale-100">
                        <button onClick={() => startEditSpeciality(s)} className="p-2 bg-white dark:bg-slate-900 text-blue-600 rounded-lg shadow-xl hover:text-blue-500 transition-colors">
                          <Edit3 size={14} />
                        </button>
                        <button onClick={() => {
                          triggerConfirm(
                            "Delete Speciality?",
                            "Are you sure you want to permanently archive this speciality?",
                            async () => {
                              setIsProcessing(true);
                              try {
                                await dbService.deleteSpeciality(s.id);
                                onUpdateSpecialities(specialities.filter(spec => spec.id !== s.id));
                                showNotification("Speciality deleted.", "success");
                              } catch (e) {
                                console.error("Delete Speciality Error:", e);
                                showNotification("Failed to delete speciality.", "error");
                              } finally {
                                setIsProcessing(false);
                              }
                            }
                          );
                        }} className="p-2 bg-white dark:bg-slate-900 text-red-600 rounded-lg shadow-xl hover:text-red-500 transition-colors">
                          <Trash2 size={14} />
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          {/* LEADS TAB */}
          {activeTab === 'leads' && (
            <div className="space-y-8">
              <h2 className="text-xl font-black text-slate-800 dark:text-white uppercase tracking-tight">Active Client Inquiries</h2>
              <div className="grid gap-5">
                {inquiries.length === 0 ? (
                  <div className="text-center py-24 bg-slate-50 dark:bg-slate-800/30 rounded-[2rem] border border-dashed">
                    <p className="text-slate-400 font-bold uppercase text-[10px] tracking-widest">Awaiting Communication</p>
                  </div>
                ) : inquiries.map((lead) => (
                  <div key={lead.id} className="bg-slate-50 dark:bg-slate-800/30 border border-slate-200 dark:border-slate-700 p-8 rounded-[2rem] flex flex-col md:flex-row justify-between items-start md:items-center gap-6 group hover:border-blue-500 transition-colors">
                    <div className="min-w-0 flex-grow">
                      <div className="flex items-center gap-3 mb-2">
                        <h4 className="font-black text-slate-900 dark:text-white uppercase tracking-tight text-base truncate">{lead.name}</h4>
                        <span className="bg-blue-100 dark:bg-blue-900/50 text-blue-800 dark:text-blue-300 text-[8px] font-black px-2 py-0.5 rounded uppercase">{lead.projectType}</span>
                      </div>
                      <p className="text-blue-600 dark:text-blue-400 text-xs font-black tracking-widest uppercase mb-4">{lead.email}</p>
                      <div className="bg-white dark:bg-slate-950 p-5 rounded-2xl border border-slate-100 dark:border-slate-800 text-slate-600 dark:text-slate-300 text-sm leading-relaxed italic">
                        "{lead.message}"
                      </div>
                    </div>
                    <div className="flex flex-col items-end gap-3 shrink-0">
                      <span className="text-[10px] font-black text-slate-400 uppercase tracking-widest whitespace-nowrap">{new Date(lead.timestamp).toLocaleString()}</span>
                      <button onClick={() => {
                        triggerConfirm(
                          "Archive Record?",
                          "Are you sure you want to archive this client record?",
                          async () => {
                            setIsProcessing(true);
                            try {
                              await dbService.deleteInquiry(lead.id);
                              loadInquiries();
                              showNotification("Inquiry archived.", "success");
                            } catch (e) {
                              console.error("Delete Inquiry Error:", e);
                              showNotification("Failed to delete inquiry.", "error");
                            } finally {
                              setIsProcessing(false);
                            }
                          }
                        );
                      }} className="p-3 text-red-500 hover:bg-red-50 dark:hover:bg-red-900/30 rounded-xl transition-colors">
                        <Trash2 size={20} />
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* CONTACT INFO TAB */}
          {activeTab === 'contact' && (
            <div className="space-y-8">
              <h2 className="text-xl font-black text-slate-800 dark:text-white uppercase tracking-tight">Global Contact Details</h2>
              <div className="p-10 bg-slate-50 dark:bg-slate-800/40 rounded-[2.5rem] border border-slate-200 dark:border-slate-700 max-w-2xl">
                <div className="space-y-6">
                  <div className="space-y-2">
                    <label className="text-[10px] font-black uppercase text-slate-400 tracking-[0.2em] ml-1">Primary Email</label>
                    <input type="email" className="w-full bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-700 rounded-xl px-5 py-4 text-sm font-bold dark:text-white focus:ring-2 focus:ring-blue-500 outline-none" value={contactForm.email} onChange={e => setContactForm({...contactForm, email: e.target.value})} />
                  </div>
                  <div className="space-y-2">
                    <label className="text-[10px] font-black uppercase text-slate-400 tracking-[0.2em] ml-1">Phone Number</label>
                    <input type="text" className="w-full bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-700 rounded-xl px-5 py-4 text-sm font-bold dark:text-white focus:ring-2 focus:ring-blue-500 outline-none" value={contactForm.phone} onChange={e => setContactForm({...contactForm, phone: e.target.value})} />
                  </div>
                  <div className="space-y-2">
                    <label className="text-[10px] font-black uppercase text-slate-400 tracking-[0.2em] ml-1">Physical Address</label>
                    <textarea rows={3} className="w-full bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-700 rounded-xl px-5 py-4 text-sm font-bold dark:text-white focus:ring-2 focus:ring-blue-500 outline-none" value={contactForm.address} onChange={e => setContactForm({...contactForm, address: e.target.value})} />
                  </div>
                  <div className="space-y-2">
                    <label className="text-[10px] font-black uppercase text-slate-400 tracking-[0.2em] ml-1">Google Maps Embed URL (Optional)</label>
                    <input type="text" className="w-full bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-700 rounded-xl px-5 py-4 text-sm font-bold dark:text-white focus:ring-2 focus:ring-blue-500 outline-none" value={contactForm.mapUrl || ''} onChange={e => setContactForm({...contactForm, mapUrl: e.target.value})} placeholder="https://www.google.com/maps/embed?..." />
                  </div>
                  <button onClick={saveContact} className="w-full bg-blue-700 text-white py-5 rounded-2xl font-black uppercase tracking-[0.2em] hover:bg-blue-600 transition-all shadow-2xl shadow-blue-900/20 active:scale-95 text-sm">Update Contact Infrastructure</button>
                </div>
              </div>
            </div>
          )}

          {activeTab === 'users' && (
            <div className="space-y-8">
              <h2 className="text-xl font-black text-slate-800 dark:text-white uppercase tracking-tight">User Access Management</h2>
              <div className="grid grid-cols-1 gap-6">
                {users.map((user: any) => (
                  <div key={user.uid} className="p-6 bg-white dark:bg-slate-800 rounded-3xl border border-slate-100 dark:border-slate-700 flex items-center justify-between group hover:border-blue-500 transition-all">
                    <div className="flex items-center gap-4">
                      <img src={user.photoURL} className="w-12 h-12 rounded-full border-2 border-slate-100 dark:border-slate-700" referrerPolicy="no-referrer" />
                      <div>
                        <h4 className="font-black text-slate-900 dark:text-white uppercase tracking-tight">{user.displayName}</h4>
                        <p className="text-xs text-slate-400 font-bold">{user.email}</p>
                        <div className="mt-1 flex items-center gap-2">
                          <span className={`text-[8px] font-black uppercase tracking-widest px-2 py-0.5 rounded-full ${
                            user.isApproved ? 'bg-green-100 text-green-700' : 'bg-amber-100 text-amber-700'
                          }`}>
                            {user.isApproved ? 'Approved' : 'Pending Approval'}
                          </span>
                          <span className="text-[8px] font-black uppercase tracking-widest px-2 py-0.5 rounded-full bg-slate-100 text-slate-600">
                            {user.role}
                          </span>
                        </div>
                      </div>
                    </div>
                    
                    {user.email !== 'radhikarans2901@gmail.com' && (
                      <button 
                        onClick={async () => {
                          setIsProcessing(true);
                          try {
                            await dbService.updateUserApproval(user.uid, !user.isApproved);
                            await loadUsers();
                            showNotification(user.isApproved ? "Access Revoked" : "Access Granted", "success");
                          } catch (e) {
                            showNotification("Failed to update status", "error");
                          } finally {
                            setIsProcessing(false);
                          }
                        }}
                        className={`px-6 py-2.5 rounded-xl text-[10px] font-black uppercase tracking-widest transition-all ${
                          user.isApproved 
                            ? 'bg-red-50 text-red-600 hover:bg-red-100' 
                            : 'bg-blue-700 text-white hover:bg-blue-600 shadow-lg shadow-blue-900/10'
                        }`}
                      >
                        {user.isApproved ? 'Revoke Access' : 'Grant Access'}
                      </button>
                    )}
                  </div>
                ))}
              </div>
            </div>
          )}
        </main>
      </div>
    </div>

      {/* CONFIRMATION MODAL */}
      {confirmModal.show && (
        <div className="fixed inset-0 bg-slate-900/80 backdrop-blur-sm z-[10000] flex items-center justify-center p-4">
          <div className="bg-white dark:bg-slate-900 w-full max-w-md rounded-[2.5rem] p-10 shadow-2xl border border-slate-100 dark:border-slate-800 animate-in fade-in zoom-in duration-300">
            <h3 className="text-2xl font-black text-slate-900 dark:text-white uppercase tracking-tight mb-4">{confirmModal.title}</h3>
            <p className="text-slate-500 dark:text-slate-400 text-sm leading-relaxed mb-10 font-medium">{confirmModal.message}</p>
            <div className="flex gap-4">
              <button 
                onClick={() => setConfirmModal(prev => ({ ...prev, show: false }))}
                className="flex-1 bg-slate-100 dark:bg-slate-800 text-slate-600 dark:text-slate-400 py-4 rounded-2xl font-black uppercase tracking-widest text-[10px] hover:bg-slate-200 transition-colors"
              >
                Cancel
              </button>
              <button 
                onClick={() => {
                  confirmModal.onConfirm();
                  setConfirmModal(prev => ({ ...prev, show: false }));
                }}
                className="flex-1 bg-red-600 text-white py-4 rounded-2xl font-black uppercase tracking-widest text-[10px] hover:bg-red-700 transition-all shadow-xl shadow-red-900/20 active:scale-95"
              >
                Confirm
              </button>
            </div>
          </div>
        </div>
      )}

      {/* NOTIFICATION TOAST */}
      {notification.show && (
        <div className={`fixed bottom-8 left-1/2 -translate-x-1/2 z-[10001] px-8 py-4 rounded-2xl font-black uppercase tracking-widest text-[10px] shadow-2xl animate-in slide-in-from-bottom-4 duration-300 flex items-center gap-3 ${
          notification.type === 'success' ? 'bg-blue-700 text-white shadow-blue-900/20' : 'bg-red-600 text-white shadow-red-900/20'
        }`}>
          {notification.type === 'success' ? <CheckCircle size={16} /> : <AlertTriangle size={16} />}
          {notification.message}
        </div>
      )}
    </>
  );
};

export default AdminDashboard;